// Had to manually format the data for local timezones, my previous approach resulted in failed tests. This solution seems more robust without the need for a library to do it.

import React, { useState, useId, useEffect } from 'react'

export interface ReactDatePickerProps {
  label?: string
  shouldShowTime?: boolean
  handleSet?: (value: Date | null) => void
  initialDate?: Date | null
  placeHolderText?: string
  showInitialDate?: boolean
  className?: string
  inputId?: string
  minDate?: string
  maxDate?: string
}

export default function DatePicker({
  label,
  handleSet,
  initialDate,
  shouldShowTime = false,
  placeHolderText = '',
  showInitialDate = false,
  className,
  inputId: providedInputId,
  minDate,
  maxDate,
}: ReactDatePickerProps) {
  const autoId = useId()
  const inputId = providedInputId || autoId

  const getDateString = (dateObj: Date | null): string => {
    if (!dateObj) return ''
    const year = dateObj.getFullYear()
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
    const day = dateObj.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getTimeString = (dateObj: Date | null): string => {
    if (!dateObj) return '00:00'
    const hours = dateObj.getHours().toString().padStart(2, '0')
    const minutes = dateObj.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  const getInitialDate = () => {
    if (initialDate !== undefined) return initialDate
    if (showInitialDate) return new Date()
    return null
  }

  const initialDateObj = getInitialDate()

  // If there's no initial date and we have a minDate, ensure the date picker
  // doesn't default to a value that's before minDate
  const getValidatedInitialDateString = () => {
    const dateStr = getDateString(initialDateObj)
    if (!dateStr && minDate) {
      // Don't set a default - let it be empty
      return ''
    }
    if (dateStr && minDate && dateStr < minDate) {
      // If initial date is before minDate, clear it
      return ''
    }
    return dateStr
  }

  const [dateValue, setDateValue] = useState<string>(getValidatedInitialDateString())
  const [timeValue, setTimeValue] = useState<string>(shouldShowTime ? getTimeString(initialDateObj) : '')

  // Ensure time value is set when shouldShowTime is true
  useEffect(() => {
    if (shouldShowTime && !timeValue) {
      setTimeValue('00:00')
    }
  }, [shouldShowTime, timeValue])

  const createDateFromInputs = (dateStr: string, timeStr: string): Date | null => {
    if (!dateStr) return null

    if (shouldShowTime && timeStr) {
      return new Date(`${dateStr}T${timeStr}`)
    } else if (shouldShowTime && !timeStr) {
      // If time is empty but date is set, default to 12:00 AM
      return new Date(`${dateStr}T00:00`)
    } else {
      const parts = dateStr.split('-')
      const year = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1
      const day = parseInt(parts[2], 10)
      return new Date(year, month, day)
    }
  }

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setDateValue(value)
    if (handleSet) {
      const newDate = createDateFromInputs(value, timeValue)
      handleSet(newDate)
    }
  }

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setTimeValue(value)
    if (handleSet) {
      const newDate = createDateFromInputs(dateValue, value)
      handleSet(newDate)
    }
  }

  return (
    <div className={`w-full datepicker-form-group ${className || ''}`}>
      {label && (
        <label htmlFor={inputId} className="datepicker-label">
          {label}
        </label>
      )}
      <div className="flex flex-row gap-1 items-center">
        <div className="w-[53%]">
          <input
            id={inputId}
            data-testid="date-picker-input"
            type="date"
            className="input w-full active:border-base-300 focus-within:outline-transparent focus-visible:outline-primary focus:outline-none focus-within:border-primary pl-1"
            value={dateValue || ''}
            placeholder={placeHolderText}
            onChange={handleDateChange}
            min={minDate}
            max={maxDate}
          />
        </div>
        {shouldShowTime && (
          <div className="w-[48%]">
            <input
              id={`${inputId}-time`}
              data-testid="date-picker-time-input"
              type="time"
              className="input w-full active:border-base-300 focus-within:outline-transparent focus-visible:outline-primary focus:outline-none focus-within:border-primary pl-1"
              value={timeValue || ''}
              placeholder={placeHolderText}
              onChange={handleTimeChange}
            />
          </div>
        )}
      </div>
    </div>
  )
}
